import {createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode} from "react";

export type Theme = "light" | "dark";

export const THEME_STORAGE_KEY = "shifath-platform-admin-theme";

export type ThemeContextValue = {
  theme: Theme;
  setTheme(theme: Theme): void;
  toggleTheme(): void;
};

type ThemeState = {
  theme: Theme;
  hasUserPreference: boolean;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);
const DARK_THEME_QUERY = "(prefers-color-scheme: dark)";

function isTheme(value: unknown): value is Theme {
  return value === "light" || value === "dark";
}

function readStoredTheme(): Theme | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

function readSystemTheme(): Theme {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return "light";
  return window.matchMedia(DARK_THEME_QUERY).matches ? "dark" : "light";
}

function createInitialState(): ThemeState {
  const storedTheme = readStoredTheme();
  return storedTheme
    ? {theme: storedTheme, hasUserPreference: true}
    : {theme: readSystemTheme(), hasUserPreference: false};
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}

export function ThemeProvider({children}: {children: ReactNode}) {
  const [state, setState] = useState<ThemeState>(createInitialState);

  useLayoutEffect(() => applyTheme(state.theme), [state.theme]);

  useEffect(() => {
    if (state.hasUserPreference || typeof window.matchMedia !== "function") return;
    const mediaQuery = window.matchMedia(DARK_THEME_QUERY);
    const syncWithSystem = (event?: MediaQueryListEvent) => {
      const matches = event?.matches ?? mediaQuery.matches;
      setState(current => current.hasUserPreference
        ? current
        : {theme: matches ? "dark" : "light", hasUserPreference: false});
    };

    syncWithSystem();
    mediaQuery.addEventListener("change", syncWithSystem);
    return () => mediaQuery.removeEventListener("change", syncWithSystem);
  }, [state.hasUserPreference]);

  const setTheme = useCallback((theme: Theme) => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // Storage can be unavailable in hardened/private browser contexts.
    }
    setState({theme, hasUserPreference: true});
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(state.theme === "dark" ? "light" : "dark");
  }, [setTheme, state.theme]);

  const value = useMemo(() => ({theme: state.theme, setTheme, toggleTheme}), [setTheme, state.theme, toggleTheme]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error("ThemeProvider missing");
  return value;
}
